/**
 * Register page component
 * Allows new users to create an account
 */

import { Check, X } from 'lucide-react';
import * as React from 'react';
import { useNavigate, Link } from 'react-router-dom';

import { AuthLayout } from '@/components/auth/AuthLayout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import { signUp } from '@/services/auth';

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

const passwordRequirements: PasswordRequirement[] = [
  {
    label: 'At least 8 characters',
    test: (pwd) => pwd.length >= 8,
  },
  {
    label: 'Contains uppercase letter',
    test: (pwd) => /[A-Z]/.test(pwd),
  },
  {
    label: 'Contains lowercase letter',
    test: (pwd) => /[a-z]/.test(pwd),
  },
  {
    label: 'Contains number',
    test: (pwd) => /\d/.test(pwd),
  },
  {
    label: 'Contains special character',
    test: (pwd) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
  },
];

export function Register() {
  const navigate = useNavigate();
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showRequirements, setShowRequirements] = React.useState(false);

  // Form validation errors
  const [firstNameError, setFirstNameError] = React.useState('');
  const [lastNameError, setLastNameError] = React.useState('');
  const [emailError, setEmailError] = React.useState('');
  const [passwordError, setPasswordError] = React.useState('');
  const [confirmPasswordError, setConfirmPasswordError] = React.useState('');

  const validateFirstName = (value: string): boolean => {
    const trimmed = value.trim();
    if (!trimmed) {
      setFirstNameError('First name is required');
      return false;
    }
    if (trimmed.length > 100) {
      setFirstNameError('First name cannot exceed 100 characters');
      return false;
    }
    setFirstNameError('');
    return true;
  };

  const validateLastName = (value: string): boolean => {
    const trimmed = value.trim();
    if (!trimmed) {
      setLastNameError('Last name is required');
      return false;
    }
    if (trimmed.length > 100) {
      setLastNameError('Last name cannot exceed 100 characters');
      return false;
    }
    setLastNameError('');
    return true;
  };

  const validateEmail = (value: string): boolean => {
    if (!value) {
      setEmailError('Email is required');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validatePassword = (value: string): boolean => {
    if (!value) {
      setPasswordError('Password is required');
      return false;
    }

    const allRequirementsMet = passwordRequirements.every((req) =>
      req.test(value)
    );

    if (!allRequirementsMet) {
      setPasswordError('Password does not meet all requirements');
      return false;
    }

    setPasswordError('');
    return true;
  };

  const validateConfirmPassword = (value: string): boolean => {
    if (!value) {
      setConfirmPasswordError('Please confirm your password');
      return false;
    }
    if (value !== password) {
      setConfirmPasswordError('Passwords do not match');
      return false;
    }
    setConfirmPasswordError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate all fields
    const isFirstNameValid = validateFirstName(firstName);
    const isLastNameValid = validateLastName(lastName);
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);
    const isConfirmPasswordValid = validateConfirmPassword(confirmPassword);

    if (!isFirstNameValid || !isLastNameValid || !isEmailValid || !isPasswordValid || !isConfirmPasswordValid) {
      return;
    }

    setIsLoading(true);

    try {
      await signUp({
        email,
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      // Navigate to email verification page on success
      navigate('/verify-email', { state: { email } });
    } catch (err) {
      console.error('Registration error:', err);
      if (err instanceof Error) {
        if (err.message.includes('UsernameExistsException')) {
          setError(
            'An account with this email already exists. Please sign in instead.'
          );
        } else if (err.message.includes('InvalidPasswordException')) {
          setError('Password does not meet security requirements.');
        } else {
          setError(err.message);
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Create account"
      subtitle="Start making better creative decisions"
      footer="By creating an account, you agree to our Terms of Service and Privacy Policy"
    >
      <div className="rounded-lg border border-border-subtle bg-surface p-8 shadow-md">
          {/* Error Alert */}
          {error && (
            <div
              className="mb-6 rounded-md border border-danger/20 bg-danger-soft px-4 py-3"
              role="alert"
              aria-live="polite"
            >
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name Fields - Side by Side */}
            <div className="grid grid-cols-2 gap-4">
              <Input
                id="firstName"
                type="text"
                label="First name"
                placeholder="Jane"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  if (firstNameError) validateFirstName(e.target.value);
                }}
                onBlur={() => validateFirstName(firstName)}
                error={!!firstNameError}
                errorMessage={firstNameError}
                disabled={isLoading}
                autoComplete="given-name"
                required
              />
              <Input
                id="lastName"
                type="text"
                label="Last name"
                placeholder="Doe"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  if (lastNameError) validateLastName(e.target.value);
                }}
                onBlur={() => validateLastName(lastName)}
                error={!!lastNameError}
                errorMessage={lastNameError}
                disabled={isLoading}
                autoComplete="family-name"
                required
              />
            </div>

            {/* Email Input */}
            <Input
              id="email"
              type="email"
              label="Email address"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) validateEmail(e.target.value);
              }}
              onBlur={() => validateEmail(email)}
              error={!!emailError}
              errorMessage={emailError}
              disabled={isLoading}
              autoComplete="email"
              required
            />

            {/* Password Input */}
            <div>
              <Input
                id="password"
                type="password"
                label="Password"
                placeholder="Create a strong password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (passwordError) validatePassword(e.target.value);
                }}
                onFocus={() => setShowRequirements(true)}
                onBlur={() => {
                  validatePassword(password);
                  setShowRequirements(false);
                }}
                error={!!passwordError}
                errorMessage={passwordError}
                disabled={isLoading}
                autoComplete="new-password"
                required
              />

              {/* Password Requirements */}
              {(showRequirements || passwordError) && password && (
                <div className="mt-3 rounded-md border border-border-subtle bg-surface-subtle p-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">
                    Password Requirements
                  </p>
                  <ul className="space-y-2" role="list">
                    {passwordRequirements.map((req, index) => {
                      const isMet = req.test(password);
                      return (
                        <li
                          key={index}
                          className="flex items-center gap-2 text-xs"
                        >
                          {isMet ? (
                            <Check className="h-3 w-3 text-success" />
                          ) : (
                            <X className="h-3 w-3 text-muted" />
                          )}
                          <span
                            className={cn(
                              isMet ? 'text-success' : 'text-secondary'
                            )}
                          >
                            {req.label}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            {/* Confirm Password Input */}
            <Input
              id="confirmPassword"
              type="password"
              label="Confirm password"
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (confirmPasswordError)
                  validateConfirmPassword(e.target.value);
              }}
              onBlur={() => validateConfirmPassword(confirmPassword)}
              error={!!confirmPasswordError}
              errorMessage={confirmPasswordError}
              disabled={isLoading}
              autoComplete="new-password"
              required
            />

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={isLoading}
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? 'Creating account...' : 'Create account'}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border-subtle"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-surface px-3 text-muted">
                Already have an account?
              </span>
            </div>
          </div>

          {/* Login Link */}
          <div className="text-center">
            <Link
              to="/login"
              className={cn(
                'inline-flex text-sm font-medium text-secondary hover:text-primary transition-colors',
                isLoading && 'pointer-events-none opacity-50'
              )}
              tabIndex={isLoading ? -1 : 0}
            >
              Sign in instead
            </Link>
          </div>
      </div>
    </AuthLayout>
  );
}
